import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Users, Mail, Phone, Package, Upload, FileText, X, Loader2, 
  Edit, Save, Eye, Download, Calendar, DollarSign, AlertCircle, 
  CheckCircle, Clock, Filter, Search
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const BillingManagement = () => {
  const [activeCustomers, setActiveCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [uploadType, setUploadType] = useState('invoice');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [customerDocuments, setCustomerDocuments] = useState([]);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    invoiceAmount: '',
    invoiceDate: '',
    dueDate: '',
    paymentStatus: 'pending'
  });
  const [showPaidInvoicesModal, setShowPaidInvoicesModal] = useState(false);
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentCustomer, setPaymentCustomer] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);

  useEffect(() => {
    fetchActiveCustomers();
  }, []);

  const fetchActiveCustomers = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/billing/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success && data.customers) {
        setActiveCustomers(data.customers);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching active customers:', error);
      toast.error('Failed to load active customers');
      setLoading(false);
    }
  };

  const fetchCustomerDocuments = async (orderId) => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/documents/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        setCustomerDocuments(data.documents);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer.orderId);
    setEditFormData({
      currentDues: customer.currentDues || 0,
      pendingInvoices: customer.pendingInvoices || 0,
      totalInvoiceAmount: customer.totalInvoiceAmount || 0,
      totalCollection: customer.totalCollection || 0,
      paymentStatus: customer.paymentStatus || 'pending',
      lastPaymentDate: customer.lastPaymentDate || '',
      notes: customer.notes || ''
    });
  };

  const handleSaveCustomer = async (orderId) => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const formData = new FormData();
      
      Object.keys(editFormData).forEach(key => {
        formData.append(key, editFormData[key]);
      });

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/billing/${orderId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Customer billing details updated!');
        setEditingCustomer(null);
        fetchActiveCustomers();
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Failed to update customer');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      setRecordingPayment(true);
      const token = localStorage.getItem('customerAdminToken');
      
      // Calculate new totals
      const currentCollection = parseFloat(paymentCustomer.totalCollection) || 0;
      const newCollection = currentCollection + parseFloat(paymentAmount);
      const totalBilled = parseFloat(paymentCustomer.totalInvoiceAmount) || 0;
      const newDues = totalBilled - newCollection;

      const formData = new FormData();
      formData.append('currentDues', newDues);
      formData.append('pendingInvoices', paymentCustomer.pendingInvoices || 0);
      formData.append('totalInvoiceAmount', totalBilled);
      formData.append('totalCollection', newCollection);
      formData.append('paymentStatus', newDues <= 0 ? 'paid' : 'partial');
      formData.append('lastPaymentDate', paymentDate);
      formData.append('notes', paymentNotes || `Payment recorded: ₹${paymentAmount}`);

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/billing/${paymentCustomer.orderId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Also record in payment_confirmations for tracking
        const paymentRecord = {
          orderId: paymentCustomer.orderId,
          customerName: paymentCustomer.fullName,
          customerEmail: paymentCustomer.email,
          amount: parseFloat(paymentAmount),
          paymentDate: paymentDate,
          notes: paymentNotes,
          recordedBy: 'admin'
        };

        await fetch(`${API_BASE_URL}/api/customer-admin/record-payment`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(paymentRecord)
        });

        toast.success(`Payment of ₹${paymentAmount} recorded successfully!`);
        setShowPaymentModal(false);
        setPaymentAmount('');
        setPaymentNotes('');
        fetchActiveCustomers();
      } else {
        toast.error(data.message || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };


  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload PDF or image files only');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (uploadType === 'invoice' && (!invoiceData.invoiceNumber || !invoiceData.invoiceAmount)) {
      toast.error('Please fill invoice details');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('customerAdminToken');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('uploadType', uploadType);
      formData.append('customerEmail', selectedCustomer.email);
      formData.append('customerName', selectedCustomer.fullName);
      formData.append('orderId', selectedCustomer.orderId);
      
      if (uploadType === 'invoice') {
        formData.append('invoiceNumber', invoiceData.invoiceNumber);
        formData.append('invoiceAmount', invoiceData.invoiceAmount);
        formData.append('invoiceDate', invoiceData.invoiceDate);
        formData.append('dueDate', invoiceData.dueDate);
        formData.append('paymentStatus', invoiceData.paymentStatus);
      }

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/upload-document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${uploadType === 'invoice' ? 'Invoice' : 'Account Statement'} uploaded successfully!`);
        setShowUploadModal(false);
        setSelectedFile(null);
        setSelectedCustomer(null);
        setInvoiceData({
          invoiceNumber: '',
          invoiceAmount: '',
          invoiceDate: '',
          dueDate: '',
          paymentStatus: 'pending'
        });
        fetchActiveCustomers();
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleViewDocuments = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDocuments(customer.orderId);
    setShowDocumentsModal(true);
  };

  const handleViewPaidInvoices = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/documents/${customer.orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        // Filter only paid invoices
        const paid = data.documents.filter(doc => 
          doc.documentType === 'invoice' && doc.paymentStatus === 'paid'
        );
        setPaidInvoices(paid);
        setShowPaidInvoicesModal(true);
      }
    } catch (error) {
      console.error('Error fetching paid invoices:', error);
      toast.error('Failed to load paid invoices');
    }
  };

  const getFilteredCustomers = () => {
    let filtered = activeCustomers;

    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.orderId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Paid' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      overdue: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle, label: 'Overdue' },
      partial: { bg: 'bg-orange-100', text: 'text-orange-800', icon: AlertCircle, label: 'Partial' }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`${badge.bg} ${badge.text} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleDownloadDocument = async (filename) => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/download-document/${filename}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Document downloaded successfully!');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Account & Billing Management</h2>
          <p className="text-gray-600 mt-1">Complete accounting system for active customers</p>
          <Button 
            onClick={() => window.location.href = '/customer-admin/suspense-amount'}
            className="mt-3 bg-orange-600 hover:bg-orange-700"
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Suspense Amount
          </Button>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="bg-blue-100 px-4 py-2 rounded-lg">
            <p className="text-sm text-blue-700">Active Customers</p>
            <p className="text-2xl font-bold text-blue-900">{activeCustomers.length}</p>
          </div>
          <div className="bg-purple-100 px-4 py-2 rounded-lg">
            <p className="text-sm text-purple-700">Total Billed</p>
            <p className="text-2xl font-bold text-purple-900">
              {formatCurrency(activeCustomers.reduce((sum, c) => sum + (parseFloat(c.totalInvoiceAmount) || 0), 0))}
            </p>
          </div>
          <div className="bg-green-100 px-4 py-2 rounded-lg">
            <p className="text-sm text-green-700">Total Collection</p>
            <p className="text-2xl font-bold text-green-900">
              {formatCurrency(activeCustomers.reduce((sum, c) => sum + (parseFloat(c.totalCollection) || 0), 0))}
            </p>
          </div>
          <div className="bg-red-100 px-4 py-2 rounded-lg">
            <p className="text-sm text-red-700">Total Dues</p>
            <p className="text-2xl font-bold text-red-900">
              {formatCurrency(activeCustomers.reduce((sum, c) => sum + (parseFloat(c.currentDues) || 0), 0))}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or order ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Time</option>
              <option value="current_month">Current Month</option>
              <option value="current_quarter">Current Quarter</option>
              <option value="last_quarter">Last Quarter</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : getFilteredCustomers().length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No customers found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {getFilteredCustomers().map((customer) => (
            <Card key={customer.orderId} className="border-2 hover:border-blue-300 transition-all">
              <CardContent className="p-6">
                {editingCustomer === customer.orderId ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b pb-3">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{customer.fullName}</h3>
                        <p className="text-sm text-gray-500">Editing Financial Details</p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleSaveCustomer(customer.orderId)} size="sm" className="bg-green-600 hover:bg-green-700">
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                        <Button onClick={() => setEditingCustomer(null)} size="sm" variant="outline">
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Total Billed Amount (₹)</Label>
                        <Input
                          type="number"
                          value={editFormData.totalInvoiceAmount}
                          onChange={(e) => {
                            const billed = parseFloat(e.target.value) || 0;
                            const collection = parseFloat(editFormData.totalCollection) || 0;
                            const dues = billed - collection;
                            setEditFormData({
                              ...editFormData, 
                              totalInvoiceAmount: e.target.value,
                              currentDues: dues
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label>Total Collection (₹)</Label>
                        <Input
                          type="number"
                          value={editFormData.totalCollection}
                          onChange={(e) => {
                            const collection = parseFloat(e.target.value) || 0;
                            const billed = parseFloat(editFormData.totalInvoiceAmount) || 0;
                            const dues = billed - collection;
                            setEditFormData({
                              ...editFormData, 
                              totalCollection: e.target.value,
                              currentDues: dues
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label>Total Dues (₹) <span className="text-xs text-gray-500">(Auto-calculated)</span></Label>
                        <Input
                          type="number"
                          value={editFormData.currentDues}
                          disabled
                          className="bg-gray-100"
                        />
                      </div>
                      <div>
                        <Label>Pending Invoices</Label>
                        <Input
                          type="number"
                          value={editFormData.pendingInvoices}
                          onChange={(e) => setEditFormData({...editFormData, pendingInvoices: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Payment Status</Label>
                        <select
                          value={editFormData.paymentStatus}
                          onChange={(e) => setEditFormData({...editFormData, paymentStatus: e.target.value})}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="paid">Paid</option>
                          <option value="pending">Pending</option>
                          <option value="overdue">Overdue</option>
                          <option value="partial">Partial Payment</option>
                        </select>
                      </div>
                      <div>
                        <Label>Last Payment Date</Label>
                        <Input
                          type="date"
                          value={editFormData.lastPaymentDate}
                          onChange={(e) => setEditFormData({...editFormData, lastPaymentDate: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <Input
                          value={editFormData.notes}
                          onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                          placeholder="Payment notes..."
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-blue-100 p-3 rounded-full">
                            <Users className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{customer.fullName}</h3>
                            <p className="text-sm text-gray-500">Order ID: {customer.orderId}</p>
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-4 gap-3 text-sm mb-4">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-gray-600 mb-1 flex items-center gap-2">
                              <Mail className="h-4 w-4" /> Email
                            </p>
                            <p className="font-semibold text-gray-900 text-xs break-all">{customer.email}</p>
                          </div>
                          
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-gray-600 mb-1 flex items-center gap-2">
                              <Phone className="h-4 w-4" /> Mobile
                            </p>
                            <p className="font-semibold text-gray-900">{customer.mobileNumber}</p>
                          </div>
                          
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-gray-600 mb-1 flex items-center gap-2">
                              <Package className="h-4 w-4" /> Service
                            </p>
                            <p className="font-semibold text-blue-700">{customer.service}</p>
                          </div>

                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-gray-600 mb-1 flex items-center gap-2">
                              <Calendar className="h-4 w-4" /> Billing Term
                            </p>
                            <p className="font-semibold text-gray-900">{customer.deviceDetails?.billingTerm || 'N/A'}</p>
                          </div>
                        </div>

                        {/* Financial Summary */}
                        <div className="grid md:grid-cols-5 gap-3 mb-4">
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border-2 border-blue-200">
                            <p className="text-xs text-blue-700 font-semibold mb-1">TOTAL BILLED AMOUNT</p>
                            <p className="text-2xl font-bold text-blue-900">{formatCurrency(customer.totalInvoiceAmount)}</p>
                          </div>

                          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border-2 border-green-200">
                            <p className="text-xs text-green-700 font-semibold mb-1">TOTAL COLLECTION</p>
                            <p className="text-2xl font-bold text-green-900">{formatCurrency(customer.totalCollection || 0)}</p>
                          </div>
                          
                          <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-lg border-2 border-red-200">
                            <p className="text-xs text-red-700 font-semibold mb-1">TOTAL DUES</p>
                            <p className="text-2xl font-bold text-red-900">{formatCurrency(customer.currentDues)}</p>
                            <p className="text-xs text-red-600 mt-1">
                              ({formatCurrency(customer.totalInvoiceAmount)} - {formatCurrency(customer.totalCollection || 0)})
                            </p>
                          </div>
                          
                          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg border-2 border-yellow-200">
                            <p className="text-xs text-yellow-700 font-semibold mb-1">PENDING INVOICES</p>
                            <p className="text-2xl font-bold text-yellow-900">{customer.pendingInvoices || 0}</p>
                          </div>

                          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border-2 border-purple-200">
                            <p className="text-xs text-purple-700 font-semibold mb-1">PAYMENT STATUS</p>
                            <div className="mt-2">{getStatusBadge(customer.paymentStatus)}</div>
                          </div>
                        </div>

                        {customer.lastPaymentDate && (
                          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                            <p className="text-sm text-blue-700">Last Payment: <span className="font-bold">{formatDate(customer.lastPaymentDate)}</span></p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          onClick={() => handleEditCustomer(customer)}
                          className="bg-blue-600 hover:bg-blue-700 gap-2"
                          size="sm"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => {
                            setPaymentCustomer(customer);
                            setShowPaymentModal(true);
                          }}
                          className="bg-green-600 hover:bg-green-700 gap-2"
                          size="sm"
                        >
                          <DollarSign className="h-4 w-4" />
                          Record Payment
                        </Button>
                        <Button
                          onClick={() => handleViewPaidInvoices(customer)}
                          className="bg-teal-600 hover:bg-teal-700 gap-2"
                          size="sm"
                        >
                          <DollarSign className="h-4 w-4" />
                          Paid Amount
                        </Button>
                        <Button
                          onClick={() => handleViewDocuments(customer)}
                          className="bg-purple-600 hover:bg-purple-700 gap-2"
                          size="sm"
                        >
                          <Eye className="h-4 w-4" />
                          View Docs
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setUploadType('invoice');
                            setShowUploadModal(true);
                          }}
                          className="bg-green-600 hover:bg-green-700 gap-2"
                          size="sm"
                        >
                          <Upload className="h-4 w-4" />
                          Invoice
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setUploadType('statement');
                            setShowUploadModal(true);
                          }}
                          className="bg-orange-600 hover:bg-orange-700 gap-2"
                          size="sm"
                        >
                          <FileText className="h-4 w-4" />
                          Statement
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <div className="flex justify-between items-center">
                <CardTitle>
                  Upload {uploadType === 'invoice' ? 'Invoice' : 'Account Statement'}
                </CardTitle>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setSelectedCustomer(null);
                  }}
                  className="text-white hover:bg-white/20 p-1 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">{selectedCustomer.fullName}</p>
                <p className="text-sm text-gray-600">{selectedCustomer.email}</p>
                <p className="text-xs text-gray-500 mt-1">Order: {selectedCustomer.orderId}</p>
              </div>

              {uploadType === 'invoice' && (
                <div className="space-y-3 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold text-gray-900">Invoice Details</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Invoice Number *</Label>
                      <Input
                        value={invoiceData.invoiceNumber}
                        onChange={(e) => setInvoiceData({...invoiceData, invoiceNumber: e.target.value})}
                        placeholder="INV-2025-001"
                      />
                    </div>
                    <div>
                      <Label>Invoice Amount (₹) *</Label>
                      <Input
                        type="number"
                        value={invoiceData.invoiceAmount}
                        onChange={(e) => setInvoiceData({...invoiceData, invoiceAmount: e.target.value})}
                        placeholder="5000"
                      />
                    </div>
                    <div>
                      <Label>Invoice Date</Label>
                      <Input
                        type="date"
                        value={invoiceData.invoiceDate}
                        onChange={(e) => setInvoiceData({...invoiceData, invoiceDate: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={invoiceData.dueDate}
                        onChange={(e) => setInvoiceData({...invoiceData, dueDate: e.target.value})}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Payment Status</Label>
                      <select
                        value={invoiceData.paymentStatus}
                        onChange={(e) => setInvoiceData({...invoiceData, paymentStatus: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                        <option value="partial">Partial Payment</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select File *
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                />
                <p className="text-xs text-gray-500">Accepted: PDF, JPG, PNG (Max 5MB)</p>
              </div>

              {selectedFile && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 font-medium">Selected: {selectedFile.name}</p>
                  <p className="text-xs text-green-600">Size: {(selectedFile.size / 1024).toFixed(2)} KB</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setSelectedCustomer(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Paid Invoices Modal */}
      {showPaidInvoicesModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="bg-gradient-to-r from-green-600 to-teal-600 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Paid Amount Details - {selectedCustomer.fullName}</CardTitle>
                  <p className="text-sm text-green-100 mt-1">
                    Total Paid: {formatCurrency(paidInvoices.reduce((sum, inv) => sum + (parseFloat(inv.invoiceAmount) || 0), 0))}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPaidInvoicesModal(false);
                    setPaidInvoices([]);
                    setSelectedCustomer(null);
                  }}
                  className="text-white hover:bg-white/20 p-1 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {paidInvoices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <DollarSign className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>No paid invoices found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Summary Cards */}
                  <div className="grid md:grid-cols-3 gap-3 mb-4">
                    <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                      <p className="text-sm text-green-700 font-semibold">Total Paid Invoices</p>
                      <p className="text-2xl font-bold text-green-900">{paidInvoices.length}</p>
                    </div>
                    <div className="bg-teal-50 p-4 rounded-lg border-2 border-teal-200">
                      <p className="text-sm text-teal-700 font-semibold">Total Amount Collected</p>
                      <p className="text-2xl font-bold text-teal-900">
                        {formatCurrency(paidInvoices.reduce((sum, inv) => sum + (parseFloat(inv.invoiceAmount) || 0), 0))}
                      </p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                      <p className="text-sm text-blue-700 font-semibold">Average Invoice Value</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {formatCurrency(paidInvoices.reduce((sum, inv) => sum + (parseFloat(inv.invoiceAmount) || 0), 0) / paidInvoices.length)}
                      </p>
                    </div>
                  </div>

                  {/* Invoice List */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-bold text-gray-900 border-b pb-2">Invoice-wise & Amount-wise Details</h3>
                    {paidInvoices.map((invoice, index) => (
                      <div key={index} className="p-4 border-2 border-green-200 rounded-lg hover:border-green-400 transition-all bg-gradient-to-r from-green-50 to-white">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="bg-green-500 p-2 rounded-full">
                                <CheckCircle className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900 text-lg">Invoice #{invoice.invoiceNumber}</p>
                                <p className="text-sm text-gray-600">Payment Status: <span className="text-green-700 font-semibold">PAID ✓</span></p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3 text-sm mt-3">
                              <div className="bg-white p-3 rounded border">
                                <p className="text-gray-600 font-semibold">Amount Paid:</p>
                                <p className="text-xl font-bold text-green-700">{formatCurrency(invoice.invoiceAmount)}</p>
                              </div>
                              {invoice.invoiceDate && (
                                <div className="bg-white p-3 rounded border">
                                  <p className="text-gray-600 font-semibold">Invoice Date:</p>
                                  <p className="font-semibold text-gray-900">{formatDate(invoice.invoiceDate)}</p>
                                </div>
                              )}
                              <div className="bg-white p-3 rounded border">
                                <p className="text-gray-600 font-semibold">Payment Received:</p>
                                <p className="font-semibold text-gray-900">{formatDate(invoice.uploadedAt)}</p>
                              </div>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="gap-2 ml-4"
                            onClick={() => handleDownloadDocument(invoice.filename)}
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Documents Modal */}
      {showDocumentsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <div className="flex justify-between items-center">
                <CardTitle>Documents - {selectedCustomer.fullName}</CardTitle>
                <button
                  onClick={() => {
                    setShowDocumentsModal(false);
                    setCustomerDocuments([]);
                    setSelectedCustomer(null);
                  }}
                  className="text-white hover:bg-white/20 p-1 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {customerDocuments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>No documents uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {customerDocuments.map((doc, index) => (
                    <div key={index} className="p-4 border-2 rounded-lg hover:border-purple-300 transition-all">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {doc.documentType === 'invoice' ? (
                              <div className="bg-green-100 p-2 rounded-full">
                                <FileText className="h-4 w-4 text-green-600" />
                              </div>
                            ) : (
                              <div className="bg-orange-100 p-2 rounded-full">
                                <FileText className="h-4 w-4 text-orange-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-gray-900 capitalize">{doc.documentType}</p>
                              <p className="text-xs text-gray-500">{doc.filename}</p>
                            </div>
                          </div>
                          
                          {doc.invoiceNumber && (
                            <div className="grid grid-cols-2 gap-2 text-sm mt-3 bg-gray-50 p-3 rounded">
                              <div>
                                <p className="text-gray-600">Invoice #:</p>
                                <p className="font-semibold">{doc.invoiceNumber}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Amount:</p>
                                <p className="font-semibold text-green-700">{formatCurrency(doc.invoiceAmount)}</p>
                              </div>
                              {doc.invoiceDate && (
                                <div>
                                  <p className="text-gray-600">Date:</p>
                                  <p className="font-semibold">{formatDate(doc.invoiceDate)}</p>
                                </div>
                              )}
                              {doc.paymentStatus && (
                                <div>
                                  <p className="text-gray-600">Status:</p>
                                  {getStatusBadge(doc.paymentStatus)}
                                </div>
                              )}
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-400 mt-2">Uploaded: {formatDate(doc.uploadedAt)}</p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-2"
                          onClick={() => handleDownloadDocument(doc.filename)}
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && paymentCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-between items-center border-b pb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Record Payment</h3>
                  <p className="text-sm text-gray-600">{paymentCustomer.fullName}</p>
                  <p className="text-xs text-gray-500 mt-1">Order ID: {paymentCustomer.orderId}</p>
                </div>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                    setPaymentNotes('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">Total Billed</p>
                    <p className="font-bold text-gray-900">{formatCurrency(paymentCustomer.totalInvoiceAmount)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Current Collection</p>
                    <p className="font-bold text-green-700">{formatCurrency(paymentCustomer.totalCollection || 0)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600">Outstanding Dues</p>
                    <p className="font-bold text-red-700 text-xl">{formatCurrency(paymentCustomer.currentDues)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Payment Amount (₹) *</Label>
                  <Input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="Enter payment amount"
                    min="0"
                    step="0.01"
                  />
                  {paymentAmount && (
                    <p className="text-xs text-gray-600 mt-1">
                      New Collection: <span className="font-semibold text-green-700">
                        {formatCurrency((parseFloat(paymentCustomer.totalCollection) || 0) + parseFloat(paymentAmount))}
                      </span>
                    </p>
                  )}
                </div>

                <div>
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                </div>

                <div>
                  <Label>Notes (Optional)</Label>
                  <Input
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Add payment notes..."
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleRecordPayment}
                  className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                  disabled={recordingPayment}
                >
                  {recordingPayment ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <DollarSign className="h-4 w-4 mr-2" />
                      Record Payment
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                    setPaymentNotes('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={recordingPayment}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BillingManagement;